//import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { Select } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import { fetchChats, sendChatMessage } from '../api/chat.js';
import { fetchEventActiveSession } from '../api/fetchEvent.js';
import { fetchParticipant } from '../api/fetchParticipant.js';
import { fetchSessionParticipants } from '../api/fetchParticipants.js';
import { fetchSession } from '../api/fetchSession.js';
import { createGuestSeat, updateAttendance } from '../api/participantEvents.js';

//import AttendanceChart from '../components/AttendanceChart';
import { fetchGathering } from '../api/fetchGathering.js';
import EmoteIcon from '../components/EmoteIcon.js';
import EmoteIconTrigger from '../components/EmoteIconTrigger.js';
import EventDetails, {
    CANCELLED,
    GAME_ON,
    MAXED_OUT,
} from '../components/EventDetails.js';
import Participation from '../components/Participation.js';
import ProfileAvatar from '../components/ProfileAvatar.js';
import { Sponsors } from '../components/Sponsors.js';
import { useChat } from '../hooks/useChat.js';
import * as currentUserUtils from '../utils/currentUserUtils.js';
import { showToast, updateToast } from '../utils/toastUtils.js';
import * as utils from '../utils/utils.js';

import 'dayjs/locale/is.js';

function Session() {
    const auth = useAuth();
    const [sessionData, setSession] = useState(null);
    const [author, setAuthor] = useState('');
    const [participant, setParticipant] = React.useState(null);
    const [participants, setParticipants] = useState(null);
    const [chats, setChats] = useState(null);
    const [chatCount, setChatCount] = useState(0);
    const [sort, setActiveSorting] = useState('attendance');
    const [attendance, setAttendanceCount] = useState(0);
    const [statusLabel, setStatusLabel] = useState(0);
    const [showMobileChat, setShowMobileChat] = useState(false);
    const [currentParticipantId, setCurrentParticipantId] = useState(null);
    const [showMoreDescription, setShowMoreDescription] = useState(false);
    const [long_description, setLongDescription] = useState('');
    const [gatheringName, setGatheringName] = React.useState('');
    const [gatheringLogo, setGatheringLogo] = React.useState('');
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [guestName, setGuestName] = useState('');
    const [typingTimeout, setTypingTimeout] = useState(null);
    const [eventId, setEventId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // Load showAgegroup state from user preference, default to false
    const [showAgegroup, setShowAgegroup] = useState(
        auth.currentUser?.show_age_groups || false
    );
    const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    //const [user, setUser] = useState([]);
    //const [profile, setProfile] = useState([]);
    let { id } = useParams();

    // Handle real-time reaction updates for the chats state
    const handleReactionUpdate = useCallback((data) => {
        const { chatId, emotion, participantId } = data;

        // Convert emotion to number to match database format and EmoteIcon expectations
        const emotionNumber = parseInt(emotion, 10);

        setChats(prevChats => {
            if (!prevChats) return prevChats;

            return prevChats.map(chat => {
                if (chat.chat_id === chatId) {
                    const updatedEmotions = chat.emotions || [];
                    const existingEmotion = updatedEmotions.find(
                        e => e.participant_id === participantId && e.emotion === emotionNumber
                    );

                    if (existingEmotion) {
                        // Remove emotion if it already exists (toggle off)
                        return {
                            ...chat,
                            emotions: updatedEmotions.filter(
                                e => !(e.participant_id === participantId && e.emotion === emotionNumber)
                            )
                        };
                    } else {
                        // Add new emotion
                        return {
                            ...chat,
                            emotions: [...updatedEmotions, { participant_id: participantId, emotion: emotionNumber }]
                        };
                    }
                }
                return chat;
            });
        });
    }, []);

    // Initialize real-time chat (only works when sessionId and eventId are available)
    const {
        messages: realtimeMessages,
        setMessages: setRealtimeMessages,
        typingUsers,
        sendMessage: sendRealtimeMessage,
        sendTypingIndicator,
        sendReaction
    } = useChat(sessionId, eventId, handleReactionUpdate);

    // Initialize session.
    const getSession = async (id) => {
        try {
            let session = undefined;

            // This page can be accessed as /session/event/[event_id], which provides a fixed url for bookmarks. Then the
            // id passed in is an event id, which needs to be converted to a session_id.
            if (window.location.href.indexOf('/session/event/') > -1) {
                const res = await fetchEventActiveSession(id);
                id = res.active_session_id;
            } else {
                // This page can also be accessed via /session/[session_id]/fromNotification=true, in which case
                // we want to show the actual session (and show the session url in the address bar) because it is
                // now historical data that we want to be available from the email notification. However, we
                // special-case this if the session hasn't started yet, because then we want the url to be
                // bookmarkable for the user, so in that case we navigate to the /session/event link instead.
                const fromEmailNotification = searchParams.get('fromNotification');
                if (fromEmailNotification) {
                    const now = new dayjs();
                    try {
                        session = await fetchSession(id);
                    } catch(exception) {
                        // TODO(finnur): Add a test for invalid session id being sent via notification.
                        if (exception.response.status === 404) {
                            // Showing the overview is better than showing a broken session page. 
                            navigate('/');
                        }
                        throw exception;
                    }
                    if (now < dayjs(session.timestamp)) {
                        navigate('/session/event/' + session.event_id);
                    } else {
                        // No further work is needed, delete the transient search param fromNotification, but stay on
                        // the Session page.
                        searchParams.delete('fromNotification');
                        setSearchParams(searchParams);
                    }
                }
            }

            const res = await fetchSession(id);
            setSession(res);

            // Set IDs for real-time functionality
            setSessionId(res.session_id);
            setEventId(res.event_id);
            session = session || await fetchSession(id);
            setSession(session);

            let currentParticipantId =
                currentUserUtils.getParticipantForOwner(session.event.owner_id);
            if (currentParticipantId != null) {
                try {
                    setParticipant(await fetchParticipant(currentParticipantId, /* currentParticipantCheck= */ true));
                } catch (exception) {
                    if (exception.response.status !== 404) {
                        throw exception;
                    }

                    // If we reach here, it means we got a 404 when trying to look up the
                    // currentParticipantId saved in the localhost map. The most likely explanation
                    // is that the Participant has been deleted, which means we'll need to remove
                    // it from the map to allow the user to log on as a different Participant in this
                    // group. 
                    currentUserUtils.deleteParticipantForOwner(session.event.owner_id);
                    currentParticipantId = null;
                }
                if (currentParticipantId) {
                    setCurrentParticipantId(currentParticipantId);
                }
            }

            setLongDescription(session.long_description); // Seperate because of issue getting length if string is empty
            // Adds Gathering name
            if (session) {
                try {
                    const gatheringData = await fetchGathering(
                        session.event.gathering_id
                    );
                    setGatheringName(gatheringData.name);
                    setGatheringLogo(gatheringData.image_url);
                    setLongDescription(session.event.long_description);

                    document.title = gatheringData.name;
                } catch (exception) {
                   /*  console.log(
                        'Error fetching gathering with id: ',
                        session.event.gathering_id,
                        exception.toString()
                    ); */
                }
            } else {
                setGatheringName(utils.DEFAULT_EVENT_NAME);
                setGatheringLogo(utils.DEFAULT_EVENT_ICON);
            }
            await getParticipants(session.session_id);
            await getChats(session.session_id);
        } catch (err) {
            console.error(err);
        }
    };

    const refreshChat = async () => {
        await getChats(sessionData.session_id);
    };

    const getParticipant = async (participant_id) => {
        if (!participant_id) {
            return;
        }
        try {
            const participant = await fetchParticipant(participant_id);
            let author = participant.name;
            let index = participant.name.indexOf(' ');
            if (index > -1) {
                author = participant.name.substring(0, index);
            }
            if (participant.nickname !== '') {
                author += ' / ' + participant.nickname;
            }

            setAuthor(author);
        } catch (exception) {
            console.log(
                'Error fetching participant with id: ',
                participant_id,
                exception.toString()
            );
        }
    };

    useEffect(() => {
        if (!participants) {
            return;
        }

        const count = utils.getAttendanceCount(participants);
        if (!sessionData.active) {
            setStatusLabel(CANCELLED);
        } else if (count >= sessionData.max_participants) {
            setStatusLabel(MAXED_OUT);
        } else if (count >= sessionData.min_participants) {
            setStatusLabel(GAME_ON);
        }
        setAttendanceCount(count);

        getParticipant(currentParticipantId);
    }, [participants, currentParticipantId]);

    // Real-time updates are handled by individual Participation components

    // Sync real-time messages with traditional chat display
    useEffect(() => {
        if (realtimeMessages && realtimeMessages.length > 0) {
            // Convert real-time messages to traditional format and merge
            const formattedMessages = realtimeMessages.map(msg => ({
                chat_id: msg.chat_id || msg.id,
                text: msg.text || msg.message,
                author: msg.author,
                timestamp: msg.timestamp,
                emotions: msg.emotions || []
            }));

            // Merge with existing chats, avoiding duplicates
            setChats(prevChats => {
                if (!prevChats) return formattedMessages;

                const existingIds = new Set(prevChats.map(chat => chat.chat_id));
                const newMessages = formattedMessages.filter(msg => !existingIds.has(msg.chat_id));

                return [...prevChats, ...newMessages].sort((a, b) =>
                    new Date(a.timestamp) - new Date(b.timestamp)
                );
            });
        }
    }, [realtimeMessages]);



    // Show/hide chat on mobile.
    const toggleChatMobile = () => {
        if (!showMobileChat) {
            // Synthesize a history state, so that the Back action doesn't close
            // the Session page while the mobile-chat window is open (closes the
            // mobile chat instead). This also adds a hint ('show-mobile-chat')
            // so that the Forward action can reopen the Mobile chat window, if
            // it was closed using Back. See implementation in handlePopState
            // (below) for details.
            window.history.pushState(
                'show-mobile-chat',
                null,
                window.location.pathname
            );
        }
        setShowMobileChat(!showMobileChat);
    };

    const sortParticipantsByName = () => {
        if (!Array.isArray(participants)) {
            return [];
        }

        setParticipants(utils.sortByName(participants));
        setActiveSorting('name');
    };

    const sortParticipantsByAttendance = () => {
        if (!Array.isArray(participants)) {
            return [];
        }

        setParticipants(utils.sortParticipantsByAttendance(participants));
        setActiveSorting('attendance');
    };

    const sortParticipantsByTime = () => {
        if (!Array.isArray(participants)) {
            return [];
        }

        setParticipants(utils.sortByTime(participants));
        setActiveSorting('time');
    };

    const sortParticipantsByAge = () => {
        if (!Array.isArray(participants)) {
            return [];
        }

        setParticipants(utils.sortByAge(participants));
        setActiveSorting('age');
    };

    // Get session data.
    useEffect(() => {
        getSession(id);
    }, [id]);

    const getChats = async (session_id) => {
        const chats = await fetchChats(session_id);
        setChatCount(chats ? chats.length : 0);
        setChats(chats);
    };

    const postChat = async () => {
        let chat = document.getElementById('chat').value;
        let author = document.getElementById('author').value;

        if (chat.length === 0 || author.length === 0) {
            showToast(t('Chat message or name missing'), '', {
                toastId: 'toastIdChatInfoMissing',
                type: 'error',
                isLoading: false,
                autoClose: 2000,
            });
            return;
        }

        // Send via real-time socket if available, otherwise fallback to traditional API
        if (sendRealtimeMessage && sessionId && eventId) {
            sendRealtimeMessage(author, chat);

            // Clear the form
            document.getElementById('chat').value = '';
            document.getElementById('author').value = '';

            // Stop typing indicator
            if (sendTypingIndicator) {
                sendTypingIndicator(author, false);
            }
        } else {
            // Fallback to traditional API
            await sendChatMessage(sessionData.session_id, author, chat)
                .then(() => {
                    document.getElementById('chat').value = '';
                    document.getElementById('author').value = '';
                    getChats(sessionData.session_id);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    };

    const getParticipants = async (session_id) => {
        const participants = await fetchSessionParticipants(session_id);
        setParticipants(utils.sortParticipantsByAttendance(participants));
    };

    // Helper function to calculate age from Icelandic kennitala
    const calculateAgeFromKennitala = (kennitala) => {
        if (!kennitala || kennitala.length < 6) return null;

        const day = parseInt(kennitala.substring(0, 2), 10);
        const month = parseInt(kennitala.substring(2, 4), 10);
        let year = parseInt(kennitala.substring(4, 6), 10);

        const centuryDigit = kennitala.length >= 10 ? kennitala[9] : '0';
        if (centuryDigit === '9' || centuryDigit === '0') {
            year += 1900;
        } else {
            year += 2000;
        }

        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    };

    // Helper function to get age group
    const getAgeGroup = (age) => {
        if (age === null || age < 20) return null;
        if (age >= 60) return '60+';
        if (age >= 50) return '50+';
        if (age >= 40) return '40+';
        if (age >= 30) return '30+';
        if (age >= 20) return '20+';
        return null;
    };

    // Calculate age group counts
    const ageGroupCounts = React.useMemo(() => {
        if (!participants) return {};

        const counts = {
            '20+': 0,
            '30+': 0,
            '40+': 0,
            '50+': 0,
            '60+': 0,
        };

        participants.forEach(participant => {
            const ssn = participant.ssn || participant.participant?.ssn;
            const age = ssn ? calculateAgeFromKennitala(ssn) : null;
            const ageGroup = getAgeGroup(age);
            if (ageGroup && counts.hasOwnProperty(ageGroup)) {
                counts[ageGroup]++;
            }
        });

        return counts;
    }, [participants]);

    // Filter participants by selected age groups
    const filteredParticipants = participants && selectedAgeGroups.length > 0
        ? participants.filter(participant => {
            const ssn = participant.ssn || participant.participant?.ssn;
            const age = ssn ? calculateAgeFromKennitala(ssn) : null;
            const ageGroup = getAgeGroup(age);
            return ageGroup && selectedAgeGroups.includes(ageGroup);
        })
        : participants;

    const userRespond = async (e) => {
        // Check if this is a real-time update (no API call needed)
        const isUpdateFromSync = e.isUpdateFromSync === true;
        const isOptimisticUpdate = e.isOptimisticUpdate === true;

/*         if (isUpdateFromSync) {
            const updateType = isOptimisticUpdate ? 'optimistic' : 'authoritative';
            console.log(`Processing Socket.IO ${updateType} update:`, {
                attendanceId: e.target.name,
                value: e.target.value,
                willBeConfirmed: e.target.value === '1'
            });
        } */

        // Check if session is active (for all updates)
        if (!sessionData.active) {
            if (!isUpdateFromSync) {
                showToast(t('This event has been cancelled'), '', {
                    toastId: 'toastIdEventCancelled',
                    type: 'error',
                    isLoading: false,
                    autoClose: 2000,
                });
                return;
            }
        }
        // Check max capacity for ALL updates (including sync updates)
        const count = utils.getAttendanceCount(participants);
        if (e.target.value === '1' && count >= sessionData.max_participants) {
            if (!isUpdateFromSync) {
                showMaxedOutToast('RadioButton');
                return;
            }
            // For sync updates, allow the update (someone else got the spot)
        }

        const newParticipants = participants.map((participant) => {
            if (participant.attendance_id === e.target.name) {
                let currentParticipant =
                    currentUserUtils.getParticipantForOwner(
                        sessionData.event.owner_id
                    );
                if (currentParticipant == null) {
                    setCurrentParticipantId(participant.participant_id);
                }

                currentUserUtils.maybeSaveParticipantForOwner(
                    sessionData.event.owner_id,
                    participant.participant_id
                );

                // Remove the old key if any (it has been deprecated).
                localStorage.removeItem('currentParticipantId');

                return {
                    ...participant,
                    confirmed: e.target.value === '1',
                    timestamp: dayjs().utc().format('YYYY-MM-DD HH:mm:ss'),
                };
            }

            return participant;
        });

        // Only make API call if this is not a sync update from Socket.IO
        if (!isUpdateFromSync) {
            try {
                await updateAttendance(
                    e.target.name,
                    e.target.value,
                    currentUserUtils.getParticipantForOwner(
                        sessionData.event.owner_id
                    ),
                    e.isFromSocketUpdate // Pass flag to indicate this came from socket
                );
            } catch (err) {
                console.log(err);
            }
        }

        setParticipants(newParticipants);
    };

    /* useEffect(() => {
         if (user.length >0) {
            axios
                .get(
                    `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${user.access_token}`,
                    {
                        headers: {
                            Authorization: `Bearer ${user.access_token}`,
                            Accept: 'application/json',
                        },
                    }
                )
                .then((res) => {
                    setProfile(res.data);
                })
                .catch((err) => console.log(err));
        }
        else {
            setProfile(null);
        }
    }, [user]); */

    /*  const login = useGoogleLogin({
        onSuccess: (codeResponse) => setUser(codeResponse),
        onError: (error) => console.log("Login Failed:", error),
    }); */

    // log out function to log the user out of google and set the profile array to null
    /*   const logOut = () => {
        googleLogout();
        setProfile(null);
    }; */

    let attendancePercentage = utils.getAttendancePercentage(
        attendance,
        sessionData !== null ? sessionData.min_participants : 1
    );

    /* useEffect(() => {
        // Simulate fetching data
        const fetchData = async () => {
            const data = [
                {
                    timestamp: '2024-06-21T10:00:00.000Z',
                    type: 'attend',
                    attendanceCount: 5,
                },
                {
                    timestamp: '2024-06-21T11:00:00.000Z',
                    type: 'leave',
                    attendanceCount: 4,
                },
                {
                    timestamp: '2024-06-21T12:00:00.000Z',
                    type: 'attend',
                    attendanceCount: 6,
                },
                {
                    timestamp: '2024-06-21T13:00:00.000Z',
                    type: 'leave',
                    attendanceCount: 5,
                },
                // Add more data as needed
            ];
        };

        fetchData();
    }, []); */

    // Handler to add a guest seat
    // Guest modal handlers
    const openGuestModal = () => {
        const count = utils.getAttendanceCount(participants);
        if (count >= sessionData.max_participants) {
            showMaxedOutToast('AddGuestButton');
        } else {
            setShowGuestModal(true);
        }
    };
    const closeGuestModal = () => {
        setShowGuestModal(false);
        setGuestName('');
    };
    const confirmAddGuest = async () => {
        const toastId = showToast(t('Adding guest …'), '', { isLoading: true });

        try {
            await createGuestSeat(sessionData.session_id, guestName);

            updateToast(toastId, '', {
                render: t('Guest seat added'),
                type: 'success',
                isLoading: false,
                autoClose: 2000, // ms
                closeOnClick: true,
            });

            closeGuestModal();
            await refreshParticipants(sessionData.session_id);
        } catch (err) {
            console.error(err);

            updateToast(toastId, '', {
                render: t('Failed to add guest seat'),
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
        }
    };

    const refreshParticipants = async (sid) => {
        const parts = await fetchSessionParticipants(sid);
        setParticipants(utils.sortParticipantsByAttendance(parts));
    };
    // Ensure that Back closes the mobile chat window (if visible), instead
    // of navigating back, and reopens it on Forward.
    useEffect(() => {
        const handlePopstate = (event) => {
            if (event.state === 'show-mobile-chat') {
                // This is a Forward button press to go back to the mobile chat.
                setShowMobileChat(true);
                return;
            }

            // Handle when Back is pressed (close the mobile chat if open) and
            // handle potentially unbalanced history state.
            if (showMobileChat) {
                setShowMobileChat(false);
            } else {
                // This can happen if the mobile-chat Close button is used,
                // instead of the Back button (then there's an extra history
                // state that hasn't been popped with the Back button, so we pop
                // it here).
                window.history.back();
            }
        };

        window.addEventListener('popstate', handlePopstate);

        return () => {
            window.removeEventListener('popstate', handlePopstate);
        };
    }, [showMobileChat]);

    const showMaxedOutToast = (idSuffix) => {
        showToast(t('This event is already maxed out'), '', {
            toastId: 'toastIdEventMaxedOut' + idSuffix,
            type: 'error',
            isLoading: false,
            autoClose: 2000,
        });
    }

    return (
        <div className="App">
            <div
                className={`chatMobile ${showMobileChat ? 'active' : ''}`}
                onClick={toggleChatMobile}
            >
                {chatCount > 0 && <div className="chatCount">{chatCount}</div>}
                <img src="/img/chat-bubble.png" alt={t('Chat')} />
            </div>
            <div
                className={`chatMobile ${!showMobileChat ? 'active' : ''}`}
                onClick={toggleChatMobile}
            >
                <img src="/img/close.png" alt={t('Close')} />
            </div>
            <div
                className="container main"
                // eslint-disable-next-line react/no-unknown-property
                sessionid={sessionData ? sessionData.session_id : 'missing'}
            >
                <div className="columns main_inner">
                    <div className="column  left-area">
                        <div className="brand">
                            <div className="logo">
                                <div className="beta-tag">Beta</div>
                                {sessionData && (
                                    <img
                                        src={gatheringLogo}
                                        alt={gatheringName}
                                    />
                                )}
                            </div>
                            <div className="name">{gatheringName}</div>
                        </div>

                        {sessionData && (
                            <EventDetails
                                name={sessionData.event.name}
                                date={sessionData.timestamp}
                                time={dayjs(sessionData.timestamp).utc().format(
                                    'HH:mm:ss'
                                )}
                                venue={sessionData.venue}
                                attendanceCount={attendance}
                                attendancePercentage={attendancePercentage}
                                statusLabel={statusLabel}
                            />
                        )}
                        {sessionData && sessionData.admin_alert && (
                            <div className="alert" id="adminAlert">
                                {sessionData.admin_alert}
                            </div>
                        )}
                        <div
                            className={`chat ${showMobileChat ? 'active' : ''}`}
                        >
                            <div className="messages">
                                {chats && (
                                    <>
                                        {chats.map((chat) => (
                                            <div
                                                className="message"
                                                key={chat.chat_id}
                                            >
                                                <div className="avatar"></div>
                                                <div
                                                    className="text"
                                                    id={'chat-' + chat.chat_id}
                                                >
                                                    {chat.text}
                                                    {chat.emotions.length >
                                                        0 && (
                                                        <>
                                                            <div>
                                                                <EmoteIcon
                                                                    emoteIconType={
                                                                        utils.EMOTION_LIKE
                                                                    }
                                                                    chatId={
                                                                        chat.chat_id
                                                                    }
                                                                    currentParticipantId={
                                                                        currentParticipantId
                                                                    }
                                                                    participants={
                                                                        participants
                                                                    }
                                                                    emotionsList={
                                                                        chat.emotions
                                                                    }
                                                                ></EmoteIcon>
                                                                <EmoteIcon
                                                                    emoteIconType={
                                                                        utils.EMOTION_HEART
                                                                    }
                                                                    chatId={
                                                                        chat.chat_id
                                                                    }
                                                                    currentParticipantId={
                                                                        currentParticipantId
                                                                    }
                                                                    participants={
                                                                        participants
                                                                    }
                                                                    emotionsList={
                                                                        chat.emotions
                                                                    }
                                                                ></EmoteIcon>
                                                                <EmoteIcon
                                                                    emoteIconType={
                                                                        utils.EMOTION_HAPPY
                                                                    }
                                                                    chatId={
                                                                        chat.chat_id
                                                                    }
                                                                    currentParticipantId={
                                                                        currentParticipantId
                                                                    }
                                                                    participants={
                                                                        participants
                                                                    }
                                                                    emotionsList={
                                                                        chat.emotions
                                                                    }
                                                                ></EmoteIcon>
                                                                <EmoteIcon
                                                                    emoteIconType={
                                                                        utils.EMOTION_SAD
                                                                    }
                                                                    chatId={
                                                                        chat.chat_id
                                                                    }
                                                                    currentParticipantId={
                                                                        currentParticipantId
                                                                    }
                                                                    participants={
                                                                        participants
                                                                    }
                                                                    emotionsList={
                                                                        chat.emotions
                                                                    }
                                                                ></EmoteIcon>
                                                                {chat.emotions
                                                                    .length > 0
                                                                    ? ' ' +
                                                                      chat
                                                                          .emotions
                                                                          .length
                                                                    : ''}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="author">
                                                    <span
                                                        className="name"
                                                        id={
                                                            'author-' +
                                                            chat.chat_id
                                                        }
                                                    >
                                                        {chat.author}
                                                    </span>{'-'}
                                                    <span
                                                        className="time"
                                                        id={
                                                            'time-' +
                                                            chat.chat_id
                                                        }
                                                    >
                                                        {dayjs(
                                                            chat.timestamp
                                                        ).utc().format(
                                                            'HH:mm - D. MMM'
                                                        )}
                                                    </span>{' '}

                                                    <span className="emoticon">
                                                        {currentParticipantId !=
                                                            null && (
                                                            <EmoteIconTrigger
                                                                chatId={
                                                                    chat.chat_id
                                                                }
                                                                currentParticipantId={
                                                                    currentParticipantId
                                                                }
                                                                emotionsList={
                                                                    chat.emotions
                                                                }
                                                                refreshChatCallback={
                                                                    refreshChat
                                                                }
                                                                sendReaction={sendReaction}
                                                            ></EmoteIconTrigger>
                                                        )}
                                                    </span>

                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {/* Typing indicators */}
                                {typingUsers && typingUsers.length > 0 && (
                                    <div className="typing-indicators">
                                        <div className="typing-message">
                                            <div className="avatar"></div>
                                            <div className="text">
                                                <em>
                                                    {typingUsers.length === 1
                                                        ? `${typingUsers[0]} is typing...`
                                                        : `${typingUsers.slice(0, -1).join(', ')} and ${typingUsers[typingUsers.length - 1]} are typing...`
                                                    }
                                                </em>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="sendMessage">
                                    <div className="label">
                                        {t('Write your message')}
                                    </div>
                                    <div className="messageInput">
                                        <textarea
                                            id="chat"
                                            rows="4"
                                            placeholder={t(
                                                'Write your message'
                                            )}
                                            onInput={() => {
                                                const authorInput = document.getElementById('author');
                                                const authorName = authorInput ? authorInput.value : '';

                                                if (sendTypingIndicator && authorName) {
                                                    // Clear previous timeout
                                                    if (typingTimeout) {
                                                        clearTimeout(typingTimeout);
                                                    }

                                                    // Send typing indicator
                                                    sendTypingIndicator(authorName, true);

                                                    // Set timeout to stop typing indicator
                                                    const newTimeout = setTimeout(() => {
                                                        sendTypingIndicator(authorName, false);
                                                    }, 2000);

                                                    setTypingTimeout(newTimeout);
                                                }
                                            }}
                                        ></textarea>
                                        {/* profile && (
                                            <div className="name">
                                                {profile.name}
                                            </div>
                                        ) */}

                                        <input
                                            type="text"
                                            placeholder={t('Sender')}
                                            defaultValue={author}
                                            id="author"
                                            name="author"
                                        />

                                        <button
                                            className="button is-primary"
                                            onClick={postChat}
                                            id="send"
                                        >
                                            {t('Send')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="sponsors desktop-only">
                                <Sponsors />
                            </div>
                        </div>
                        <div className="version">Zoolander 1.0</div>
                    </div>
                    <div className="column is-two-thirds right-area">
                        <div className="profile">
                            {participant && (
                                <div className="profile-menu">
                                    <ProfileAvatar
                                        participant={participant}
                                        newFunctionality={true}
                                    />
                                </div>
                            )}
                            {/* profile ? (
                                <>
                                    <div className="img">
                                        <img src={profile.picture} alt="user" />
                                    </div>
                                    <p>{profile.name} </p>

                                    <div onClick={logOut}> Útskrá</div>
                                </>
                            ) : (
                                <div onClick={() => login()}>Innskrá 🚀 </div>
                            ) */}
                        </div>
                        {long_description && long_description.length !== 0 && (
                            <>
                                {long_description.length > 249 ? (
                                    <>
                                        <div className="description">
                                            <p>
                                                {showMoreDescription
                                                    ? long_description
                                                    : `${long_description.substring(
                                                          0,
                                                          250
                                                      )}`}
                                            </p>
                                            <span
                                                className="btn"
                                                onClick={() =>
                                                    setShowMoreDescription(
                                                        !showMoreDescription
                                                    )
                                                }
                                            >
                                                {showMoreDescription
                                                    ? ' ...minna'
                                                    : ' ...meira'}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="description">
                                        <p>{long_description}</p>
                                    </div>
                                )}
                            </>
                        )}

                        {/*  <div className="attendance-chart">
                            <AttendanceChart data={attendanceData} />
                        </div> */}
                        <div className="session-header">
                            <div className="sort">
                                <span>{t('Sort by')}</span>
                                <span
                                    id="sortByAttendance"
                                    onClick={sortParticipantsByAttendance}
                                    tabIndex="0"
                                    className={
                                        sort === 'attendance' ? 'active' : ''
                                    }
                                >
                                    {t('Attendance')}
                                </span>
                                <span> | </span>
                                <span
                                    id="sortByName"
                                    onClick={sortParticipantsByName}
                                    tabIndex="0"
                                    className={sort === 'name' ? 'active' : ''}
                                >
                                    {t('Alphabetically')}
                                </span>
                                <span> | </span>
                                <span
                                    id="sortByTime"
                                    onClick={sortParticipantsByTime}
                                    tabIndex="0"
                                    className={sort === 'time' ? 'active' : ''}
                                >
                                    {t('Time')}
                                </span>
                                {auth.currentUser?.show_age_groups && (
                                    <>
                                        <span> | </span>
                                        <span
                                            id="sortByAge"
                                            onClick={sortParticipantsByAge}
                                            tabIndex="0"
                                            className={sort === 'age' ? 'active' : ''}
                                        >
                                            {t('Age')}
                                        </span>
                                    </>
                                )}
                            </div>
                            {auth.currentUser?.show_age_groups && sort === 'age' && (
                                <div className="age-group-controls">
                                    <div className="age-group-toggle">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={showAgegroup}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setShowAgegroup(checked);
                                                    // Note: This is a temporary override for this session only
                                                    // The user's global preference is set in Settings
                                                    if (!checked) {
                                                        setSelectedAgeGroups([]);
                                                    }
                                                }}
                                            />
                                            <span>{t('Show age groups')}</span>
                                        </label>
                                    </div>
                                    {showAgegroup && (
                                        <div className="age-group-filter">
                                            <Select
                                                mode="multiple"
                                                placeholder={t("Filter by age")}
                                                value={selectedAgeGroups}
                                                onChange={setSelectedAgeGroups}
                                                size="small"
                                                style={{ minWidth: '140px' }}
                                                maxTagCount="responsive"
                                                allowClear
                                                options={[
                                                    { value: '20+', label: `20+ (${ageGroupCounts['20+'] || 0})` },
                                                    { value: '30+', label: `30+ (${ageGroupCounts['30+'] || 0})` },
                                                    { value: '40+', label: `40+ (${ageGroupCounts['40+'] || 0})` },
                                                    { value: '50+', label: `50+ (${ageGroupCounts['50+'] || 0})` },
                                                    { value: '60+', label: `60+ (${ageGroupCounts['60+'] || 0})` },
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="participant-controls">
                                {sessionData && sessionData.event.allow_guests && (
                                    <div className="guest-seat-controls">
                                        <div
                                            id="addGuest"
                                            className="add-guest"
                                            onClick={openGuestModal}
                                            disabled={
                                                !sessionData || !sessionData.active
                                            }
                                        >
                                            <span className="icon">
                                                <img
                                                    src="/img/icon-add-user.svg"
                                                    alt="Add guest seat"
                                                    style={{
                                                        marginRight: '2px',
                                                    }}
                                                    width="12"
                                                    height="12"
                                                />
                                            </span>
                                            {t('Add guest seat')}
                                        </div>
                                    </div>
                                )}
                                
                            </div>
                        </div>

                        {/* Guest Seat Modal */}
                        {showGuestModal && (
                            <div className="modal is-active">
                                <div
                                    className="modal-background"
                                    onClick={closeGuestModal}
                                ></div>
                                <div className="modal-card">
                                    <header className="modal-card-head">
                                        <p className="modal-card-title">
                                            {t('Add guest')}
                                        </p>
                                        <button
                                            className="delete"
                                            aria-label="close"
                                            onClick={closeGuestModal}
                                        ></button>
                                    </header>
                                    <section className="modal-card-body">
                                        <div className="field">
                                            <label className="label">
                                                {t('Guest Name')}
                                            </label>
                                            <div className="control">
                                                <input
                                                    className="input"
                                                    type="text"
                                                    value={guestName}
                                                    onChange={(e) =>
                                                        setGuestName(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={t(
                                                        'Enter name'
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </section>
                                    <footer className="modal-card-foot">
                                        <button
                                            className="button is-success"
                                            onClick={confirmAddGuest}
                                        >
                                            {t('Add')}
                                        </button>
                                        <button
                                            className="button"
                                            onClick={closeGuestModal}
                                        >
                                            {t('Cancel')}
                                        </button>
                                    </footer>
                                </div>
                            </div>
                        )}

                        {filteredParticipants && (
                            <div className="participation">
                                {filteredParticipants.map((participant) => (
                                    <Participation
                                        key={participant.attendance_id}
                                        participant={participant}
                                        onChange={userRespond}
                                        currentParticipantId={
                                            currentParticipantId
                                        }
                                        ownerId={sessionData.event.owner_id}
                                        eventId={sessionData.event.event_id}
                                        sessionId={sessionData.session_id}
                                        showAgegroup={showAgegroup}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Subtle ReEvent Branding */}
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '32px 16px 16px',
                                marginTop: '24px',
                                borderTop: '1px solid #f0f0f0',
                            }}
                        >
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: '#999',
                                    margin: '0 0 8px 0',
                                }}
                            >
                                This event is powered by{' '}
                                <a
                                    href="https://reevent.io"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#86c983',
                                        textDecoration: 'none',
                                        fontWeight: '500',
                                    }}
                                >
                                    reevent.io
                                </a>
                            </p>
                            <p
                                style={{
                                    fontSize: '12px',
                                    color: '#bbb',
                                    margin: 0,
                                }}
                            >
                                <a
                                    href="https://reevent.io"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#bbb',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Create your own recurring event
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="sponsors mobile-only">
                        <Sponsors />
                    </div>
                </div>
            </div>
            <div className="footer"></div>
        </div>
    );
}

export default Session;
